import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import i18n, { DEFAULT_LOCALE } from '@/utils/i18n';
import React, { useRef, useState } from 'react';
import {
  BOTTOM_NAV_BAR_HIDE_TIMING_CONFIG,
  BOTTOM_NAV_BAR_REVEAL_TIMING_CONFIG,
  DEFAULT_EASING_IN_OUT_CONFIG,
} from '@experiences/blocs/utils/animations';
import {
  Button,
  IconButton,
  Icons,
  Input,
  ModalSheet,
  shadowStyle,
  TIMING_PRESETS,
  Typography,
} from '@usepepper/sprout-kit-ui';
import { CheckInReminderSheet } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/account-detail-check-in/components/check-in-reminder-sheet';
import { CheckInTagRow } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/account-detail-check-in/components/check-in-tag-row';
import { Coordinate } from '@/experiences/sales-portal/components/map/types';
import { createStyles } from '@/contexts/theme-context';
import { formatDateTimeSelection } from '@/experiences/sales-portal/blocs/utils/date-picker-calendar-utils';
import { Keyboard, Pressable, ScrollView, TextInput, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { LocationPermissionPrimer } from '@/experiences/sales-portal/components/location-permission-primer/location-permission-primer';
import { readCheckInLocation } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/account-detail-check-in/blocs/utils/read-check-in-location';
import { toCheckInInteractions } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/account-detail-check-in/blocs/transformers/to-check-in-interactions';
import { useCheckInForm } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/account-detail-check-in/blocs/hooks/use-check-in-form';
import { useCreateCrmActivityMutator } from '@/experiences/sales-portal/blocs/mutators/use-create-crm-activity-mutator';
import { useDeferredUnmount } from '@/experiences/sales-portal/blocs/hooks/use-deferred-unmount';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';
import { useLocationPermissionPriming } from '@/experiences/sales-portal/blocs/hooks/use-location-permission-priming';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const FAB_SIZE = 56;
const ICON_SIZE = 24;
const LABEL_MAX_WIDTH = 240;
const NOTE_MIN_HEIGHT = 120;

interface AccountDetailCheckInProps {
  accountName: string;
  chatUUID: string;
  onSaved: () => void;
}

export const AccountDetailCheckIn: React.FC<AccountDetailCheckInProps> = ({
  accountName,
  chatUUID,
  onSaved,
}) => {
  const [styles, theme] = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [isPrimerOpen, setIsPrimerOpen] = useState(false);
  const locationRef = useRef<Coordinate | null>(null);
  const openIdRef = useRef(0);
  const isSubmittingRef = useRef(false);
  const scrollRef = useRef<ScrollView>(null);
  const noteInputRef = useRef<TextInput>(null);
  const shouldRenderSheet = useDeferredUnmount(
    isOpen,
    TIMING_PRESETS.takeoverSheet.duration,
  );
  const shouldRenderReminderSheet = useDeferredUnmount(
    isReminderOpen,
    TIMING_PRESETS.takeoverSheet.duration,
  );
  const shouldRenderPrimer = useDeferredUnmount(
    isPrimerOpen,
    TIMING_PRESETS.takeoverSheet.duration,
  );
  const { canSave, form, reset, setNote, setReminder, toggleTag } =
    useCheckInForm();
  const { acceptPriming, shouldPrime } = useLocationPermissionPriming();
  const { showErrorToast, showToast } = useExperienceToasts();
  const { createCrmActivity, loading: isSaving } = useCreateCrmActivityMutator({
    onError: () =>
      showErrorToast(i18n.t('sales_portal.account_detail.check_in.error')),
  });

  const bottomNavHeight = useExperienceUIStore(
    (state) => state.bottomNavHeight,
  );
  const isBottomNavHidden = useExperienceUIStore(
    (state) => state.isBottomNavHidden,
  );
  const insets = useSafeAreaInsets();

  const label = i18n.t('sales_portal.account_detail.check_in.button');
  const description = i18n.t(
    'sales_portal.account_detail.check_in.description',
  );
  const noteLabel = i18n.t('sales_portal.account_detail.check_in.note_label');
  const notePlaceholder = i18n.t(
    'sales_portal.account_detail.check_in.note_placeholder',
  );
  const saveLabel = i18n.t('sales_portal.account_detail.check_in.save');
  const setReminderLabel = i18n.t(
    'sales_portal.activity_note.reminder.set_reminder',
  );
  const reminderLabel = form.reminder
    ? `${setReminderLabel}: ${formatDateTimeSelection(form.reminder)}`
    : setReminderLabel;

  const readLocation = () => {
    const openId = openIdRef.current;
    readCheckInLocation((location) => {
      if (openIdRef.current === openId) {
        locationRef.current = location;
      }
    });
  };

  const handleOpen = () => {
    openIdRef.current += 1;
    locationRef.current = null;
    reset();
    noteInputRef.current?.clear();
    setIsOpen(true);
    if (shouldPrime) {
      setIsPrimerOpen(true);
      return;
    }
    readLocation();
  };

  const handlePrimerContinue = async () => {
    setIsPrimerOpen(false);
    await acceptPriming();
    readLocation();
  };

  const handlePrimerDismiss = () => setIsPrimerOpen(false);

  const handleNoteContentSizeChange = () => {
    if (noteInputRef.current?.isFocused()) {
      scrollRef.current?.scrollToEnd();
    }
  };

  const handleSave = async () => {
    if (isSubmittingRef.current) {
      return;
    }
    if (form.reminder?.time && form.reminder.time <= new Date()) {
      showErrorToast(i18n.t('sales_portal.activity_note.reminder.past_error'));
      return;
    }

    isSubmittingRef.current = true;
    const note = form.note.trim();
    const activityUUID = await createCrmActivity({
      chatUUID,
      interactions: toCheckInInteractions(form.tags),
      location: locationRef.current ?? undefined,
      note:
        note ||
        i18n.t('sales_portal.activity_notes.visit_no_note', {
          locale: DEFAULT_LOCALE,
        }),
      remindAt: form.reminder?.time,
    });

    isSubmittingRef.current = false;
    if (!activityUUID) {
      return;
    }

    showToast({
      title: i18n.t('sales_portal.account_detail.check_in.success'),
      variant: 'info',
    });
    onSaved();
    setIsOpen(false);
  };

  const expandProgress = useDerivedValue(() =>
    withTiming(isBottomNavHidden ? 0 : 1, DEFAULT_EASING_IN_OUT_CONFIG),
  );
  const hideOffset = useDerivedValue(() =>
    withTiming(
      isBottomNavHidden ? Math.max(bottomNavHeight - insets.bottom, 0) : 0,
      isBottomNavHidden
        ? BOTTOM_NAV_BAR_HIDE_TIMING_CONFIG
        : BOTTOM_NAV_BAR_REVEAL_TIMING_CONFIG,
    ),
  );

  const { spaceMedium, spaceSmall } = theme.sk.spacing;
  const containerStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: hideOffset.value }],
  }));
  const labelStyle = useAnimatedStyle(() => ({
    marginLeft: expandProgress.value * spaceSmall,
    maxWidth: expandProgress.value * LABEL_MAX_WIDTH,
    opacity: expandProgress.value,
  }));

  return (
    <>
      <Animated.View
        pointerEvents="box-none"
        style={[
          styles.container,
          { bottom: bottomNavHeight + spaceMedium },
          containerStyle,
        ]}
      >
        <Pressable
          accessibilityLabel={label}
          accessibilityRole="button"
          onPress={handleOpen}
          style={({ hovered, pressed }) => [
            styles.pill,
            hovered || pressed ? styles.pillActive : null,
          ]}
        >
          <View style={styles.pillContent}>
            <Icons.MapPinCheckInside
              color={theme.sk.color.icon.primary}
              size={ICON_SIZE}
            />
            <Animated.View style={[styles.label, labelStyle]}>
              <Typography
                color="$textPrimary"
                numberOfLines={1}
                size="$md"
                variant="label"
              >
                {label}
              </Typography>
            </Animated.View>
          </View>
        </Pressable>
      </Animated.View>
      {shouldRenderSheet ? (
        <ModalSheet
          aria-label={label}
          onOpenChange={(open) => {
            if (!open && !isSaving) {
              setIsOpen(false);
            }
          }}
          open={isOpen}
        >
          <ModalSheet.Header
            closeLabel={i18n.t('close')}
            rightAction={
              <IconButton
                aria-label={reminderLabel}
                icon={<Icons.Bell />}
                indicator={form.reminder ? <IconButton.Indicator /> : <></>}
                onPress={() => {
                  Keyboard.dismiss();
                  setIsReminderOpen(true);
                }}
                shape="circle"
                size="$lg"
                variant="tertiary"
              />
            }
          />
          <KeyboardAvoidingView
            behavior="padding"
            keyboardVerticalOffset={insets.top}
            style={styles.keyboardAvoiding}
          >
            <ModalSheet.Body>
              <ScrollView
                contentContainerStyle={styles.form}
                keyboardShouldPersistTaps="handled"
                ref={scrollRef}
              >
                <View style={styles.header}>
                  <Typography
                    accessibilityRole="header"
                    color="$textPrimary"
                    fontWeight="$medium"
                    numberOfLines={1}
                    size="$xl"
                  >
                    {accountName}
                  </Typography>
                  <Typography color="$textSecondary" size="$sm" variant="body">
                    {description}
                  </Typography>
                </View>
                <CheckInTagRow
                  onToggleTag={toggleTag}
                  selectedTags={form.tags}
                />
                <Input
                  accessibilityLabel={noteLabel}
                  flexGrow={1}
                  fontSize="$lg"
                  minHeight={NOTE_MIN_HEIGHT}
                  multiline
                  onChangeText={setNote}
                  onContentSizeChange={handleNoteContentSizeChange}
                  placeholder={notePlaceholder}
                  ref={noteInputRef}
                  textAlignVertical="top"
                  variant="ghost"
                />
              </ScrollView>
            </ModalSheet.Body>
            <ModalSheet.Footer>
              <Button
                accessibilityLabel={saveLabel}
                disabled={!canSave || !isOpen}
                loading={isSaving}
                onPress={handleSave}
                size="$lg"
                variant="primary"
                width="100%"
              >
                {saveLabel}
              </Button>
            </ModalSheet.Footer>
          </KeyboardAvoidingView>
        </ModalSheet>
      ) : null}
      {shouldRenderPrimer ? (
        <LocationPermissionPrimer
          onContinue={handlePrimerContinue}
          onDismiss={handlePrimerDismiss}
          open={isPrimerOpen}
        />
      ) : null}
      {shouldRenderReminderSheet ? (
        <CheckInReminderSheet
          onClose={() => setIsReminderOpen(false)}
          onInvalidTime={() =>
            showErrorToast(
              i18n.t('sales_portal.activity_note.reminder.past_time_error'),
            )
          }
          onSetReminder={setReminder}
          open={isReminderOpen}
          reminder={form.reminder}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    position: 'absolute',
    right: theme.sk.spacing.spaceMedium,
  },
  form: {
    flexGrow: 1,
    gap: theme.sk.spacing.spaceMedium,
  },
  header: {
    gap: theme.sk.spacing.spaceXsmall,
  },
  keyboardAvoiding: {
    flex: 1,
  },
  label: {
    overflow: 'hidden',
  },
  pill: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusRound,
    borderWidth: 1,
    height: FAB_SIZE,
    minWidth: FAB_SIZE,
    ...shadowStyle('soft2', 6),
  },
  pillActive: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  pillContent: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
}));
